import styled from '@emotion/styled';
import { Button, Tag, Tooltip } from 'antd';
import {
  SmileOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import {
  PhrasesTextElement,
  PhrasesStickerElement,
  PhrasesResourceElement,
  PhraseAttachmentElement,
} from 'services/api/phrase';
import useFormatShortKey from 'views/Setting/Phrase/hooks/useFormatShortKey';
import { useMemo, forwardRef, useRef, useImperativeHandle, Ref } from 'react';
import { useMemoizedFn } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import { css } from '@emotion/css';
import { isUndefined } from 'lodash-es';
import { PhraseValue } from 'hooks/business/usePhraseOnChat';
import EnhanceText from 'components/Dialogue/components/Message/EnhanceText';

const Container = styled.div<{ sideMode?: boolean }>`
  position: relative;
  padding: 6px 12px;
  margin: 6px 12px;
  border: 1px solid var(--fett-border-default);
  background: var(--fett-bg-panel);

  &.active {
    background-color: ${({ sideMode }) =>
      !sideMode ? 'var(--fett-bg-app)' : 'var(--fett-bg-active)'};
    border-color: ${({ sideMode }) =>
      !sideMode ? 'var(--fett-border-default)' : 'var(--fett-app-primary-color)'};
  }
`;
const TextContainer = styled.div`
  flex: 1;
  overflow: hidden;

  > div + div {
    color: var(--fett-text-tertiary);
  }
`;
const ImageContainer = styled.div`
  padding: 0 5px;
  background: var(--fett-bg-elevated);
  border: 1px solid var(--fett-border-strong);
  border-radius: 2px;
  color: var(--fett-text-secondary);

  svg {
    color: var(--fett-border-strong);
  }

  span {
    margin-left: 2px;
  }

  &:not(:first-child) {
    margin-left: 8px;
  }
`;
const footer = css`
  display: flex;
  padding-top: 6px;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--fett-border-default);
  &.active {
    border-top: 1px solid var(--fett-border-strong);
  }
`;
const TwoLine = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

export interface PhraseItemProps {
  data: PhraseValue;
  region: General.UpperCaseRegion;
  active?: boolean;
  hideTag?: boolean;
  width?: number;
  hideTranslateContent?: boolean;
  mercadoQAmode?: boolean;
  sideMode?: boolean;
  onEditClick?: (text: PhrasesTextElement) => void;
  onSendClick?: (data: PhraseValue) => void;
  onMouseMove?: () => void;
  onMouseLeave?: () => void;
}

export interface PhraseItemRef {
  scrollTo: () => void;
}

export const getPhraseTextElement = (data: PhraseValue) => {
  const { phraseElems } = data;
  const text = phraseElems?.elems?.find?.((elem) => (elem as PhrasesTextElement)?.text);
  return {
    text: text as PhrasesTextElement | undefined,
    onlyText: !isUndefined(text) && phraseElems?.elems.length === 1,
  };
};

const PhraseItemImplement = (props: PhraseItemProps, ref: Ref<PhraseItemRef>) => {
  const {
    data,
    active,
    hideTag,
    width,
    hideTranslateContent,
    mercadoQAmode = false,
    sideMode = false,
    onMouseMove,
    onEditClick,
    onSendClick,
    onMouseLeave,
  } = props;
  const formatShortKey = useFormatShortKey();
  const { t } = useTranslate();

  const elRef = useRef<HTMLDivElement | null>(null);

  const { text: textEl, onlyText: onlyTextEl } = useMemo(() => {
    return getPhraseTextElement(data);
  }, [data]);

  const renderEl = useMemo(() => {
    let images = 0;
    let sticker = 0;
    let videos = 0;
    let attachments = 0;
    let textContent = '';
    let textTranslateContent = '';

    data?.phraseElems.elems.forEach((el) => {
      if ((el as PhrasesTextElement).text) {
        const { text } = el as PhrasesTextElement;
        textContent = text.content;
        textTranslateContent = text.translationContent;
      }
      if ((el as PhrasesStickerElement).sticker) {
        sticker += 1;
      }

      if ((el as PhrasesResourceElement).resource) {
        const { resource } = el as PhrasesResourceElement;
        if (resource.resourceType === 1) {
          images += 1;
        }
        if (resource.resourceType === 2) {
          videos += 1;
        }
      }
      if ((el as PhraseAttachmentElement).attachment) {
        attachments += (el as PhraseAttachmentElement).attachment.attachments.length;
      }
    });

    return {
      images,
      sticker,
      videos,
      attachments,
      textContent,
      textTranslateContent,
    };
  }, [data]);

  const handleSend = useMemoizedFn((e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    onSendClick?.(data);
  });

  useImperativeHandle(ref, () => ({
    scrollTo: () => {
      elRef.current?.scrollIntoView(false);
    },
  }));

  return (
    <Container
      ref={elRef}
      className={active ? 'active' : ''}
      style={{ width }}
      sideMode={sideMode}
      onMouseMove={(e) => {
        e.stopPropagation();
        onMouseMove?.();
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        onMouseLeave?.();
      }}
    >
      <div className="flex pb-1">
        <TextContainer>
          {hideTranslateContent ? (
            <Tooltip
              title={<EnhanceText text={renderEl.textTranslateContent || renderEl.textContent} />}
            >
              <TwoLine>
                <EnhanceText text={renderEl.textTranslateContent || renderEl.textContent} />
              </TwoLine>
            </Tooltip>
          ) : (
            <>
              <Tooltip title={<EnhanceText text={renderEl.textTranslateContent} />}>
                <TwoLine>
                  <EnhanceText text={renderEl.textTranslateContent} />
                </TwoLine>
              </Tooltip>
              <Tooltip title={<EnhanceText text={renderEl.textContent} />}>
                <TwoLine>
                  <EnhanceText text={renderEl.textContent} />
                </TwoLine>
              </Tooltip>
            </>
          )}
        </TextContainer>
        <div className="flex items-center ml-2">
          {renderEl.images ? (
            <ImageContainer>
              <PictureOutlined />
              <span>{renderEl.images}</span>
            </ImageContainer>
          ) : null}
          {renderEl.sticker ? (
            <ImageContainer>
              <SmileOutlined />
              <span>{renderEl.sticker}</span>
            </ImageContainer>
          ) : null}
          {renderEl.videos ? (
            <ImageContainer>
              <VideoCameraOutlined />
              <span>{renderEl.videos}</span>
            </ImageContainer>
          ) : null}
          {renderEl.attachments > 0 &&
            (mercadoQAmode ? (
              <Tooltip
                title={t('scene.chatWorkspace.messageEditor.validation.mercadoQaNoAttachment')}
              >
                <ImageContainer className="cursor-not-allowed bg-fill-300">
                  <LinkOutlined />
                  <span>{renderEl.attachments}</span>
                </ImageContainer>
              </Tooltip>
            ) : (
              <ImageContainer>
                <LinkOutlined />
                <span>{renderEl.attachments}</span>
              </ImageContainer>
            ))}
        </div>
      </div>
      <div className={`${footer} ${active ? 'active' : ''}`}>
        <div>
          {hideTag ? null : (
            <>
              {data.tags.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
              {data.shortcutKey && <Tag>{formatShortKey(data.shortcutKey)}</Tag>}
            </>
          )}
        </div>
        <div className="flex gap-2">
          {textEl && onlyTextEl && !mercadoQAmode && (
            <Button
              type={!sideMode ? 'primary' : undefined}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEditClick?.(textEl);
              }}
            >
              {t('scene.chatWorkspace.quickReply.action.sendAfterEditing')}
            </Button>
          )}
          <Button type="primary" size="small" onClick={handleSend}>
            {mercadoQAmode ? t('reply.应用') : t('common.action.send')}
          </Button>
        </div>
      </div>
    </Container>
  );
};

export default forwardRef(PhraseItemImplement);
